// components/chat/SavedChats.tsx
import React, { useEffect } from 'react';
import { Card, CardBody, Button, Text, AlertVariant, Tooltip } from '@patternfly/react-core';
import { faTrash, faClock, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../../components/util/Loader';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { ChatActions } from '../../api/lambda/ChatActions';
import { useApplication } from '../../components/user/ApplicationProvider';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';

export interface SavedChat {
	session_id: string;
	job_id: string;
	title: string;
	created_at: string;
	last_activity: string;
	numberOfMsgs: number;
}

interface SavedChatsProps {
	onSelectChat: (sessionId: string, jobId: string) => void;
	onNewChat: () => void;
	isLoading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	fetchSavedChats: () => Promise<void>;
	setSavedChats: React.Dispatch<React.SetStateAction<SavedChat[]>>;
	savedChats: SavedChat[];
}

export const SavedChats: React.FC<SavedChatsProps> = ({
	onSelectChat,
	onNewChat,
	isLoading = false,
	fetchSavedChats,
	setSavedChats,
	savedChats,
}) => {
	useEffect(() => {
		void fetchSavedChats();
	}, []);
	const { user } = useApplication();
	const { addToast } = useToast();

	const handleDeleteChat = async (sessionId: string) => {
		const userIdentifer = user.account + getEnvironmentCode();
		const success = await ChatActions.deleteInovationAIChatSession(sessionId, userIdentifer);
		if (success) {
			setSavedChats((prev) => prev.filter((chat) => chat.session_id !== sessionId));
			addToast('Recent chat deleted Succesfully', AlertVariant.success);
		} else {
			console.error('Failed to delete chat session:', sessionId);
		}
	};

	const chartInstructionPattern = /\s*STRICT HIGHCHARTS RULES[\s\S]*/g;

	return (
		<Card className="saved-chats-container">
			<CardBody
				style={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%', // Full height of card
					padding: 0, // Remove default padding
				}}
			>
				{/* Header */}
				<div style={{ padding: '1rem 1rem 0.5rem 1rem', marginBottom: '5px' }}>
					<Text
						component="h3"
						style={{
							margin: 0,
							fontSize: '14px',
							fontWeight: 600,
							color: '#333',
						}}
					>
						<FontAwesomeIcon
							icon={faClock}
							style={{ marginRight: '8px' }}
						/>
						Recents
						<hr />
					</Text>
				</div>
				{isLoading && <Loader size="md" />}
				{/* Scrollable chat list */}
				{!isLoading && (
					<div
						style={{
							flex: 1, // Takes remaining space
							overflowY: 'auto', // Scrollable
							padding: '0.5rem 1rem',
						}}
					>
						{savedChats.length === 0 ? (
							<Text
								component="p"
								style={{ color: '#999', fontSize: '13px' }}
							>
								No conversations yet.
							</Text>
						) : (
							<div className="saved-chats-list">
								{savedChats.map((chat) => {
									const cleanTitle = chat.title
										.replace(chartInstructionPattern, '')
										.trim();
									return (
										<div
											key={chat.session_id}
											className="saved-chat-item"
											onClick={() =>
												onSelectChat(chat.session_id, chat.job_id)
											}
											style={{
												padding: '10px',
												marginBottom: '8px',
												borderRadius: '6px',
												cursor: 'pointer',
												transition: 'background-color 0.2s',
												backgroundColor: '#f9f9f9',
												border: '1px solid #e0e0e0',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.backgroundColor = '#f0f0f0';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.backgroundColor = '#f9f9f9';
											}}
										>
											<div style={{ flex: 1, minWidth: 0 }}>
												<Text
													component="p"
													style={{
														margin: 0,
														fontSize: '13px',
														fontWeight: 500,
														color: '#333',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
													title={cleanTitle}
												>
													{cleanTitle}
												</Text>
											</div>
											{/* Delete button - fixed width */}
											<Tooltip content="Delete Recent Chat">
												<div
													style={{
														width: '32px',
														display: 'flex',
														justifyContent: 'center',
														alignItems: 'center',
														flexShrink: 0,
														marginLeft: '8px',
													}}
												>
													<Button
														variant="plain"
														style={{ padding: '4px' }}
														onClick={(e) => {
															e.stopPropagation();
															void handleDeleteChat(chat.session_id);
														}}
													>
														<FontAwesomeIcon
															icon={faTrash}
															style={{
																fontSize: '12px',
																color: '#999',
															}}
														/>
													</Button>
												</div>
											</Tooltip>
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}

				{/* Fixed bottom button */}
				<div
					style={{
						padding: '1rem',
						borderTop: '1px solid #e0e0e0',
						backgroundColor: '#fff',
					}}
				>
					<Button
						variant="secondary"
						onClick={onNewChat}
						style={{
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '6px',
							fontSize: '13px',
						}}
					>
						<FontAwesomeIcon icon={faPlus} />
						New Chat
					</Button>
				</div>
			</CardBody>
		</Card>
	);
};
